import React, { useCallback, useContext } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import omit from 'lodash/omit';
import { IteratorHelper } from 'choerodon-ui/dataset';
import { pxToRem } from '../../../lib/_util/UnitConvertor';
import { defaultAggregationRenderer } from './Column';
import TableContext from './TableContext';
import { getColumnLock, getEditorByColumnAndRecord, isStickySupport } from './utils';
import { ColumnLock } from './enum';
import TableCellInner from './TableCellInner';
import { treeSome } from '../_util/treeUtils';
import TableGroupCellInner from './TableGroupCellInner';
import { groupedAggregationTree } from './AggregationTree';
import AggregationTreeGroups from './AggregationTreeGroups';
function getRowSpan(group, tableStore) {
    if (tableStore.headerTableGroups.length) {
        const { subGroups, subHGroups } = group;
        if (subHGroups) {
            return IteratorHelper.iteratorReduce(subHGroups.values(), (rowSpan, group) => Math.max(rowSpan, group.records.length), 0);
        }
        return subGroups.reduce((rowSpan, subGroup) => rowSpan + (subGroup.subGroups.length ? getRowSpan(subGroup, tableStore) : 0) + subGroup.records.length, 0);
    }
    return group.expandedRecords.length;
}
const TableCell = function TableCell(props) {
    const { columnGroup, record, isDragging, provided, colSpan, className, children, disabled, inView, groupPath, rowIndex } = props;
    const { column, key } = columnGroup;
    const { tableStore, prefixCls, dataSet, expandIconAsCell, aggregation: tableAggregation, rowHeight } = useContext(TableContext);
    const cellPrefix = `${prefixCls}-cell`;
    const tableColumnOnCell = tableStore.getConfig('tableColumnOnCell');
    const { __tableGroup } = column;
    const [group, isLast] = __tableGroup && groupPath && groupPath.find(([path]) => path.name === __tableGroup.name) || [undefined, false];
    const [rowGroup] = group || !groupPath || !groupPath.length ? [undefined, false] : groupPath[groupPath.length - 1];
    const getInnerNode = useCallback((col, onCellStyle, inAggregation, headerGroup) => record ? (React.createElement(TableCellInner, { column: col, record: record, style: onCellStyle, disabled: disabled, inAggregation: inAggregation, prefixCls: cellPrefix, colSpan: colSpan, headerGroup: headerGroup, rowGroup: rowGroup }, children)) : undefined, [record, disabled, children, cellPrefix, colSpan, rowGroup]);
    const aggregationTreeRenderer = useCallback(({ colGroup, style }) => {
        return getInnerNode(colGroup.column, style, true, colGroup.headerGroup);
    }, [getInnerNode]);
    const { style, lock, onCell, aggregation } = column;
    const columnLock = isStickySupport() && getColumnLock(lock);
    const baseStyle = (() => {
        if (columnLock) {
            if (columnLock === ColumnLock.left) {
                return {
                    ...style,
                    left: pxToRem(columnGroup.left, true),
                };
            }
            if (columnLock === ColumnLock.right) {
                return {
                    ...style,
                    right: pxToRem(colSpan && colSpan > 1 ? 0 : columnGroup.right, true),
                };
            }
        }
        return style;
    })();
    const baseClassName = classNames(cellPrefix, {
        [`${cellPrefix}-fix-${columnLock}`]: columnLock,
    });
    if (!record) {
        return (React.createElement("td", { className: baseClassName, style: baseStyle, "data-index": key, colSpan: colSpan }));
    }
    const indexInGroup = group ? group.expandedRecords.indexOf(record) : -1;
    const groupCell = indexInGroup === 0 || tableStore.virtual && indexInGroup > -1 && tableStore.virtualStartIndex === rowIndex;
    if (group && !groupCell) {
        return null;
    }
    const getAggregationTreeGroups = ($aggregation) => {
        if ($aggregation) {
            const { childrenInAggregation } = columnGroup;
            if (childrenInAggregation) {
                const groups = childrenInAggregation.columns;
                return groupedAggregationTree({
                    rowGroup: __tableGroup ? group : rowGroup,
                    headerGroup: columnGroup.headerGroup,
                    record,
                    groups,
                    column,
                    renderer: aggregationTreeRenderer,
                });
            }
        }
    };
    const groupRowSpan = groupCell && group ? getRowSpan(group, tableStore) - indexInGroup : undefined;
    const rowSpan = groupRowSpan === 1 ? undefined : groupRowSpan;
    const renderInnerNode = ($aggregation, onCellStyle) => {
        if (expandIconAsCell && children) {
            return (React.createElement("span", { className: classNames(`${cellPrefix}-inner`, { [`${cellPrefix}-inner-row-height-fixed`]: rowHeight !== 'auto' }), style: { textAlign: 'center', ...onCellStyle } }, children));
        }
        const aggregationList = getAggregationTreeGroups($aggregation);
        if (groupCell && group && __tableGroup) {
            return (React.createElement(TableGroupCellInner, { rowSpan: rowSpan, group: group, column: column }, aggregationList));
        }
        if (aggregationList) {
            const { renderer = defaultAggregationRenderer } = column;
            const treeGroups = React.createElement(AggregationTreeGroups, { trees: aggregationList });
            return (React.createElement("span", { className: `${cellPrefix}-inner` }, renderer({
                text: treeGroups,
                record,
                dataSet,
                aggregation: tableAggregation,
                headerGroup: columnGroup.headerGroup,
                rowGroup,
                aggregationTree: aggregationList,
            })));
        }
        return getInnerNode(column, onCellStyle, undefined, columnGroup.headerGroup);
    };
    const scope = groupCell ? 'row' : undefined;
    const TCell = scope ? 'th' : 'td';
    const isBuiltInColumn = tableStore.isBuiltInColumn(column);
    const columnOnCell = !isBuiltInColumn && (onCell || tableColumnOnCell);
    const cellExternalProps = typeof columnOnCell === 'function'
        ? columnOnCell({
            dataSet,
            record,
            column,
        })
        : {};
    if (inView === false || columnGroup.inView === false) {
        const hasEditor = aggregation ? treeSome(column.children || [], (node) => !!getEditorByColumnAndRecord(node, record)) : !!getEditorByColumnAndRecord(column, record);
        const emptyCellProps = {
            colSpan,
            rowSpan,
            'data-index': key,
            ...cellExternalProps,
            className: classNames(baseClassName, cellExternalProps.className),
            style: { ...baseStyle, ...cellExternalProps.style },
            scope,
        };
        if (hasEditor) {
            emptyCellProps.onFocus = (e) => {
                tableStore.activeEmptyCell = e.target;
            };
            emptyCellProps.onBlur = () => {
                delete tableStore.activeEmptyCell;
            };
            emptyCellProps.tabIndex = 0;
        }
        return React.createElement(TCell, Object.assign({}, emptyCellProps));
    }
    const cellStyle = {
        ...baseStyle,
        ...cellExternalProps.style,
        ...(provided && { cursor: 'move' }),
    };
    const classString = classNames(baseClassName, {
        [`${cellPrefix}-aggregation`]: aggregation,
        [`${cellPrefix}-last-group`]: groupCell && isLast,
    }, column.className, className, cellExternalProps.className);
    const widthDraggingStyle = () => {
        const draggingStyle = {};
        if (isDragging) {
            const dom = tableStore.node.element.querySelector(`.${prefixCls}-tbody .${prefixCls}-cell[data-index="${key}"]`);
            if (dom) {
                draggingStyle.width = dom.clientWidth;
                draggingStyle.whiteSpace = 'nowrap';
            }
        }
        return draggingStyle;
    };
    // 只有全局属性时候的样式可以继承给下级满足对td的样式能够一致表现
    const onCellStyle = !isBuiltInColumn && tableColumnOnCell === columnOnCell && typeof tableColumnOnCell === 'function' ? omit(cellExternalProps.style, ['width', 'height']) : undefined;
    return (React.createElement(TCell, Object.assign({ colSpan: colSpan, rowSpan: rowSpan }, cellExternalProps, { className: classString, "data-index": key }, (provided && provided.dragHandleProps), { style: { ...omit(cellStyle, ['width', 'height']), ...widthDraggingStyle() }, scope: scope }), renderInnerNode(aggregation, onCellStyle)));
};
TableCell.displayName = 'TableCell';
export default observer(TableCell);
